import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Search, Building2, Plus, X, Check, Clock, Inbox, Send, Users, Sparkles, Briefcase, Rocket,
  Bell, CheckCircle2,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { PageHeader, EmptyState, Spinner, Pill } from "@/components/ui-bits";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { INDUSTRIES } from "@/lib/constants";

function StatPill({ icon: Icon, children }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground">
      <Icon size={13} className="text-primary" /> {children}
    </span>
  );
}

function StatusTag({ status }) {
  const { t } = useTranslation();
  if (status === "in_network") return <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-primary bg-primary/10 rounded-full px-2 py-0.5"><Check size={10} /> {t("expert.networks.tagMember")}</span>;
  if (status === "outgoing") return <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-warning bg-warning/10 rounded-full px-2 py-0.5"><Clock size={10} /> {t("expert.networks.tagRequested")}</span>;
  if (status === "incoming") return <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-warning bg-warning/10 rounded-full px-2 py-0.5"><Inbox size={10} /> {t("expert.networks.tagInvitedYou")}</span>;
  return null;
}

function CompanyCard({ c, i, onRequest, onLeave, onGoRequests }) {
  const { t } = useTranslation();
  const status = c.status;
  return (
    <div data-testid={`company-${c.company_id}`}
      className={`card-milled rounded-[0.8rem] p-5 animate-fade-up d${(i % 6) + 1} transition-transform duration-200 ease-out hover:-translate-y-0.5 hover:shadow-soft flex flex-col ${status === "in_network" ? "ring-1 ring-primary/40" : ""}`}>
      <div className="flex items-start gap-3">
        <span className="grid place-items-center w-12 h-12 rounded-xl bg-primary/10 text-primary shrink-0"><Building2 size={22} /></span>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm leading-tight line-clamp-2">{c.name}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{c.industry || "—"}</p>
          <div className="mt-1.5"><StatusTag status={status} /></div>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5">
        {c.size && <Pill className="bg-secondary text-muted-foreground"><Users size={11} className="mr-1" />{t("expert.networks.employeesCount", { count: c.size })}</Pill>}
        <Pill className="bg-secondary text-muted-foreground">{t("expert.networks.expertsCount", { count: c.network_size })}</Pill>
      </div>
      <div className="mt-auto pt-4 border-t border-border">
        {status === "in_network" ? (
          <Button data-testid={`leave-network-${c.company_id}`} size="sm" variant="outline" onClick={() => onLeave(c.company_id)} className="w-full rounded-lg gap-1.5"><X size={14} /> {t("expert.networks.leave")}</Button>
        ) : status === "outgoing" ? (
          <Button data-testid={`req-pending-${c.company_id}`} size="sm" variant="outline" disabled className="w-full rounded-lg gap-1.5 text-muted-foreground"><Clock size={14} /> {t("expert.networks.requestSent")}</Button>
        ) : status === "incoming" ? (
          <Button data-testid={`req-respond-${c.company_id}`} size="sm" onClick={onGoRequests} className="w-full rounded-lg gap-1.5"><Inbox size={14} /> {t("expert.networks.respondInvitation")}</Button>
        ) : (
          <Button data-testid={`request-join-${c.company_id}`} size="sm" onClick={() => onRequest(c.company_id)} className="w-full rounded-lg gap-1.5 active:scale-[0.98] transition-transform"><Plus size={14} /> {t("expert.networks.requestToJoin")}</Button>
        )}
      </div>
    </div>
  );
}

function RequestRow({ r, kind, onAccept, onDecline, onWithdraw }) {
  const { t } = useTranslation();
  const c = r.company || {};
  return (
    <div data-testid={`req-${kind}-${r.request_id}`} className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-border p-4 hover:bg-secondary/30 transition-colors">
      <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary/10 text-primary shrink-0"><Building2 size={20} /></span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium truncate">{c.name}</p>
        <p className="text-xs text-muted-foreground truncate">{c.industry || "—"} · {t("expert.networks.expertsCount", { count: c.network_size })}</p>
        {r.message && <p className="text-xs text-muted-foreground italic mt-1 line-clamp-1">“{r.message}”</p>}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {kind === "incoming" ? (
          <>
            <Button data-testid={`decline-req-${r.request_id}`} size="sm" variant="outline" onClick={() => onDecline(r.request_id)} className="rounded-lg gap-1.5 text-destructive hover:text-destructive hover:bg-destructive/5"><X size={14} /> {t("expert.networks.pending") && t("common.decline")}</Button>
            <Button data-testid={`accept-req-${r.request_id}`} size="sm" onClick={() => onAccept(r.request_id)} className="rounded-lg gap-1.5 active:scale-[0.98] transition-transform"><Check size={14} /> {t("common.accept")}</Button>
          </>
        ) : (
          <>
            <span className="inline-flex items-center gap-1 text-xs font-medium text-warning"><Clock size={13} /> {t("expert.networks.pending")}</span>
            <Button data-testid={`withdraw-req-${r.request_id}`} size="sm" variant="outline" onClick={() => onWithdraw(r.request_id)} className="rounded-lg gap-1.5">{t("expert.networks.withdraw")}</Button>
          </>
        )}
      </div>
    </div>
  );
}

export default function ExpertNetworks() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [companies, setCompanies] = useState(null);
  const [requests, setRequests] = useState({ incoming: [], outgoing: [] });
  const [waitlisted, setWaitlisted] = useState(null);
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState("all");
  const [tab, setTab] = useState("companies");

  const load = () => {
    api.get("/expert/companies").then(({ data }) => setCompanies(data)).catch(() => setCompanies([]));
    api.get("/expert/network-requests").then(({ data }) => setRequests(data)).catch(() => {});
    api.get("/expert/discovery-waitlist").then(({ data }) => setWaitlisted(data.joined)).catch(() => setWaitlisted(false));
  };
  useEffect(() => { load(); }, []);

  const joinWaitlist = async () => { try { await api.post("/expert/discovery-waitlist"); setWaitlisted(true); toast.success(t("expert.networks.toastWaitlisted")); } catch (err) { toast.error(apiError(err)); } };
  const leaveWaitlist = async () => { try { await api.delete("/expert/discovery-waitlist"); setWaitlisted(false); toast.success(t("expert.networks.toastWaitlistLeft")); } catch (err) { toast.error(apiError(err)); } };

  const request = async (id) => { try { const { data } = await api.post(`/expert/companies/${id}/request`, {}); toast.success(data.accepted ? t("expert.networks.toastJoined") : t("expert.networks.toastRequestSent")); load(); } catch (err) { toast.error(apiError(err)); } };
  const leave = async (id) => { try { await api.post(`/expert/companies/${id}/leave`, {}); toast.success(t("expert.networks.toastLeft")); load(); } catch (err) { toast.error(apiError(err)); } };
  const accept = async (rid) => { try { await api.post(`/expert/network-requests/${rid}/accept`); toast.success(t("expert.networks.toastJoined")); load(); } catch (err) { toast.error(apiError(err)); } };
  const decline = async (rid) => { try { await api.post(`/expert/network-requests/${rid}/decline`); toast.success(t("expert.networks.toastDeclined")); load(); } catch (err) { toast.error(apiError(err)); } };
  const withdraw = async (rid) => { try { await api.delete(`/expert/network-requests/${rid}`); toast.success(t("expert.networks.toastWithdrawn")); load(); } catch (err) { toast.error(apiError(err)); } };

  const reqCount = (requests.incoming?.length || 0) + (requests.outgoing?.length || 0);
  const myCount = (companies || []).filter((c) => c.status === "in_network").length;

  const displayed = useMemo(() => {
    if (!companies) return [];
    const s = q.toLowerCase();
    return companies.filter((c) => {
      if (tab === "mynetworks") return c.status === "in_network";
      if (tab === "companies") return c.status !== "in_network";
      return true;
    }).filter((c) =>
      (industry === "all" || c.industry === industry) &&
      (!s || (c.name || "").toLowerCase().includes(s)));
  }, [companies, q, industry, tab]);

  return (
    <AppShell>
      <PageHeader eyebrow={t("expert.eyebrow")} title={t("expert.networks.title")} description={t("expert.networks.subtitle")} />

      <div className="flex flex-wrap gap-2 mb-5">
        <StatPill icon={Users}>{t("expert.networks.statJoined", { count: myCount })}</StatPill>
        <StatPill icon={Building2}>{t("expert.networks.statCompanies", { count: companies?.length || 0 })}</StatPill>
        {reqCount > 0 && <StatPill icon={Send}>{t("expert.networks.statRequests", { count: reqCount })}</StatPill>}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-5">
        <TabsList className="rounded-lg">
          <TabsTrigger value="companies" data-testid="tab-discover" className="rounded-md gap-1.5"><Sparkles size={14} /> {t("expert.networks.discover")}</TabsTrigger>
          <TabsTrigger value="requests" data-testid="tab-requests" className="rounded-md gap-1.5">
            <Send size={14} /> {t("expert.networks.requests")} {reqCount > 0 && <span className="min-w-5 h-5 px-1 grid place-items-center rounded-full bg-warning/15 text-warning text-[11px] font-semibold">{reqCount}</span>}
          </TabsTrigger>
          <TabsTrigger value="mynetworks" data-testid="tab-network" className="rounded-md gap-1.5">
            <Users size={14} /> {t("expert.networks.myNetworks")} {myCount > 0 && <span className="min-w-5 h-5 px-1 grid place-items-center rounded-full bg-primary/10 text-primary text-[11px] font-semibold">{myCount}</span>}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === "requests" ? (
        (requests.incoming?.length || requests.outgoing?.length) ? (
          <div className="grid lg:grid-cols-2 gap-6 items-start">
            <section className="card-milled rounded-[0.8rem] p-6" data-testid="requests-incoming">
              <div className="flex items-center gap-2 mb-4"><span className="grid place-items-center w-8 h-8 rounded-lg bg-secondary/80 text-primary"><Inbox size={16} /></span>
                <div><h3 className="text-sm font-semibold text-foreground">{t("expert.networks.invitationsReceived")}</h3><p className="text-xs text-muted-foreground">{t("expert.networks.invitationsReceivedSub")}</p></div></div>
              {requests.incoming?.length ? <div className="space-y-3">{requests.incoming.map((r) => <RequestRow key={r.request_id} r={r} kind="incoming" onAccept={accept} onDecline={decline} />)}</div>
                : <p className="text-sm text-muted-foreground py-6 text-center">{t("expert.networks.noInvitations")}</p>}
            </section>
            <section className="card-milled rounded-[0.8rem] p-6" data-testid="requests-outgoing">
              <div className="flex items-center gap-2 mb-4"><span className="grid place-items-center w-8 h-8 rounded-lg bg-secondary/80 text-primary"><Send size={16} /></span>
                <div><h3 className="text-sm font-semibold text-foreground">{t("expert.networks.requestsSent")}</h3><p className="text-xs text-muted-foreground">{t("expert.networks.requestsSentSub")}</p></div></div>
              {requests.outgoing?.length ? <div className="space-y-3">{requests.outgoing.map((r) => <RequestRow key={r.request_id} r={r} kind="outgoing" onWithdraw={withdraw} />)}</div>
                : <p className="text-sm text-muted-foreground py-6 text-center">{t("expert.networks.noPendingRequests")}</p>}
            </section>
          </div>
        ) : (
          <EmptyState icon={Send} title={t("expert.networks.noOpenRequests")} description={t("expert.networks.noOpenRequestsDesc")} />
        )
      ) : tab === "companies" ? (
        <div data-testid="discover-coming-soon" className="card-milled rounded-[0.8rem] px-6 py-16 flex flex-col items-center text-center animate-fade-up">
          <span className="grid place-items-center w-16 h-16 rounded-2xl bg-primary/10 text-primary"><Rocket size={26} strokeWidth={1.75} /></span>
          <span className="mt-5 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-primary bg-primary/10 rounded-full px-2.5 py-1"><Clock size={12} /> {t("expert.networks.comingSoonBadge")}</span>
          <h3 className="text-lg font-medium text-foreground mt-4">{t("expert.networks.comingSoonTitle")}</h3>
          <p className="text-sm text-muted-foreground mt-2 max-w-md leading-relaxed">
            {t("expert.networks.comingSoonDesc")}
          </p>
          {waitlisted ? (
            <div className="mt-6 flex flex-col items-center gap-2" data-testid="waitlist-joined">
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-success bg-success/10 rounded-full px-3 py-1.5"><CheckCircle2 size={15} /> {t("expert.networks.onLaunchList")}</span>
              <p className="text-xs text-muted-foreground">{t("expert.networks.onLaunchListDesc", { email: user?.email ? ` (${user.email})` : "" })}</p>
              <button onClick={leaveWaitlist} data-testid="waitlist-leave" className="text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground mt-1">{t("expert.networks.removeFromList")}</button>
            </div>
          ) : (
            <div className="mt-6 flex flex-col sm:flex-row items-center gap-3">
              <Button className="rounded-lg gap-1.5" onClick={joinWaitlist} data-testid="waitlist-join"><Bell size={15} /> {t("expert.networks.notifyMe")}</Button>
              <Button variant="outline" className="rounded-lg gap-1.5 bg-card" onClick={() => setTab("requests")} data-testid="coming-soon-see-requests"><Send size={15} /> {t("expert.networks.seeInvitations")}</Button>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="company-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("expert.networks.searchPlaceholder")} className="h-11 rounded-lg pl-9" />
            </div>
            <Select value={industry} onValueChange={setIndustry}>
              <SelectTrigger data-testid="industry-filter" className="h-11 rounded-lg sm:w-56 gap-1.5"><Briefcase size={14} className="text-muted-foreground" /><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("expert.networks.allIndustries")}</SelectItem>
                {INDUSTRIES.map((ind) => <SelectItem key={ind} value={ind}>{ind}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {companies === null ? (
            <div className="grid place-items-center py-24"><Spinner className="text-primary w-7 h-7" /></div>
          ) : displayed.length === 0 ? (
            <EmptyState icon={Users} title={t("expert.networks.emptyTitle")} description={t("expert.networks.emptyDesc")} />
          ) : (
            <>
              <p className="text-xs text-muted-foreground mb-3">{t("expert.networks.companyCount", { count: displayed.length })}</p>
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {displayed.map((c, i) => (
                  <CompanyCard key={c.company_id} c={c} i={i} onRequest={request} onLeave={leave} onGoRequests={() => setTab("requests")} />
                ))}
              </div>
            </>
          )}
        </>
      )}
    </AppShell>
  );
}
